import {
  AppsOutlined,
  ArrowForward,
  Close,
  Fullscreen,
  Groups,
  NotificationsActive,
  ViewColumn,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_Row,
  MRT_SortingState,
  MRT_VisibilityState,
} from 'material-react-table';
import { useCallback, useContext, useMemo, useState } from 'react';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import SwitchTable from 'switch-fe-shared/components/Tables/SwitchTable';
import { AlertState } from 'switch-shared/modules/crud/alert/alert.enum';
import { UtilityTypeOrAll } from 'switch-shared/modules/crud/utility/utility.enums';
import { MetricBreakdown } from 'switch-shared/modules/dashboard/dashboard.dto';

import { formatNumber } from '../../../../../../switch-shared/src/utils/formatters';
import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import { useDashboard } from '../../../../context/dashboardContext';
import { useAdminUrlNavigation } from '../../../../hooks/useAdminUrlNavigation';
import { useNavigateWithState } from '../../../../hooks/useNavigateWithState';
import { useSelectedApplicationGroupId } from '../../../../hooks/useSelectedApplicationGroupId';
import { useSelectedApplicationId } from '../../../../hooks/useSelectedApplicationId';
import pages from '../../../../router/routes';
import { useAlertFindQuery } from '../../../../services/switch-rest-api/alerts.service';
import { useGetApplicationGroupOverviewQuery } from '../../../../services/switch-rest-api/dashboard.service';
import { isAllSelection, isUtilityType } from '../../../../utils/selection';
import AlertsModal from '../modals/AlertsModal';

type TableRow = {
  _id: string;
  name: string;
  groupId: string;
  meters: MetricBreakdown;
  gateways: MetricBreakdown;
  activeAlerts: number;
  assignedUsers: number;
};

interface MetricCellProps {
  metrics: MetricBreakdown;
  type: 'meters' | 'gateways';
  groupId: string;
  applicationId: string;
  utilityType?: UtilityTypeOrAll;
  onNavigate: (groupId: string, applicationId: string, path: string) => void;
}

const MetricCell = ({
  metrics,
  type,
  groupId,
  applicationId,
  utilityType,
  onNavigate,
}: MetricCellProps) => {
  const theme = useTheme();
  const { getApplicationScopedPath } = useAdminUrlNavigation();
  const hasUnits = metrics.total > 0;
  const percentageColor =
    metrics.onlinePercentage >= 90
      ? theme.palette.success.main
      : metrics.onlinePercentage >= 70
        ? theme.palette.warning.main
        : theme.palette.error.main;

  const getFilteredPath = (isOnline: boolean) => {
    const sectionPath = type === 'meters' ? 'meters' : 'gateways';
    const legacyPath =
      type === 'meters' ? pages.meters.path : pages.gateways.path;
    const path = getApplicationScopedPath(
      sectionPath,
      legacyPath,
      groupId,
      applicationId
    );
    const params = new URLSearchParams();
    const filters = [
      ...(type === 'meters' && utilityType && isUtilityType(utilityType)
        ? [`utilityType:${utilityType}`]
        : []),
      `isOnline:${isOnline}`,
    ];
    params.set('filters', filters.join(','));
    return `${path}?${params.toString()}`;
  };

  return (
    <Stack direction='row' alignItems='center' spacing={1}>
      <Typography variant='body2'>{formatNumber(metrics.total, 0)}</Typography>
      {hasUnits && (
        <Tooltip
          slotProps={{
            tooltip: {
              sx: {
                background: 'white',
                color: theme.tokens.colors.cardForeground,
                boxShadow: theme.tokens.shadows.lg,
                borderRadius: theme.tokens.borderRadius.md,
                padding: 0,
                minWidth: 175,
              },
            },
          }}
          title={
            <Box>
              <Stack
                spacing={1}
                sx={{
                  px: `${theme.tokens.spacing.md}px`,
                  py: `${theme.tokens.spacing.sm}px`,
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={2}
                >
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: theme.tokens.borderRadius.full,
                        backgroundColor: theme.palette.success.main,
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Online
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: theme.tokens.typography.fontWeight.semibold,
                      }}
                    >
                      {formatNumber(metrics.online, 0)}
                    </Typography>
                    <Button
                      size='small'
                      variant='text'
                      onClick={(event) => {
                        event.stopPropagation();
                        onNavigate(
                          groupId,
                          applicationId,
                          getFilteredPath(true)
                        );
                      }}
                      sx={{
                        minWidth: 'auto',
                        px: 1,
                        py: '1px',
                      }}
                      aria-label='View'
                    >
                      <ArrowForward fontSize='small' />
                    </Button>
                  </Stack>
                </Stack>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={2}
                >
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: theme.tokens.borderRadius.full,
                        backgroundColor: theme.palette.error.main,
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: '0.875rem',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Offline
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: theme.tokens.typography.fontWeight.semibold,
                      }}
                    >
                      {formatNumber(metrics.offline, 0)}
                    </Typography>
                    <Button
                      size='small'
                      variant='text'
                      onClick={(event) => {
                        event.stopPropagation();
                        onNavigate(
                          groupId,
                          applicationId,
                          getFilteredPath(false)
                        );
                      }}
                      sx={{
                        minWidth: 'auto',
                        px: 1,
                        py: '1px',
                      }}
                      aria-label='View'
                    >
                      <ArrowForward fontSize='small' />
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          }
        >
          <Chip
            size='small'
            label={`${metrics.onlinePercentage}%`}
            sx={{
              backgroundColor: percentageColor,
              color: theme.palette.common.white,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
              cursor: 'default',
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

interface AlertCellProps {
  count: number;
  applicationId: string;
  applicationName: string;
  onViewAlerts: (applicationId: string, applicationName: string) => void;
}

const AlertCell = ({
  count,
  applicationId,
  applicationName,
  onViewAlerts,
}: AlertCellProps) => {
  const theme = useTheme();
  const hasAlerts = count > 0;

  const handleClick = () => {
    onViewAlerts(applicationId, applicationName);
  };

  const tooltipContent = hasAlerts ? (
    <Box>
      <Stack
        spacing={1}
        sx={{
          px: `${theme.tokens.spacing.md}px`,
          py: `${theme.tokens.spacing.sm}px`,
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={2}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <NotificationsActive
              sx={{
                fontSize: '1rem',
                color: theme.palette.error.main,
              }}
            />
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Active Alert{count !== 1 ? 's' : ''}
            </Typography>
          </Stack>
          <Typography
            variant='body2'
            sx={{
              fontWeight: theme.tokens.typography.fontWeight.semibold,
            }}
          >
            {formatNumber(count, 0)}
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          px: `${theme.tokens.spacing.sm}px`,
          py: `${theme.tokens.spacing.sm}px`,
        }}
      >
        <Button
          size='small'
          variant='contained'
          endIcon={<ArrowForward />}
          onClick={handleClick}
          fullWidth
          sx={{ fontSize: theme.tokens.typography.fontSize.xs }}
        >
          View Alerts
        </Button>
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        px: `${theme.tokens.spacing.md}px`,
        py: `${theme.tokens.spacing.sm}px`,
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: theme.tokens.borderRadius.full,
            backgroundColor: theme.palette.success.main,
          }}
        />
        <Typography
          variant='body2'
          sx={{
            color: theme.palette.text.secondary,
          }}
        >
          No active alerts
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            background: 'white',
            color: theme.tokens.colors.cardForeground,
            boxShadow: theme.tokens.shadows.lg,
            borderRadius: theme.tokens.borderRadius.md,
            padding: 0,
            minWidth: 175,
          },
        },
      }}
      title={tooltipContent}
    >
      <Stack direction='row' alignItems='center' spacing={0.5}>
        {hasAlerts && (
          <NotificationsActive
            fontSize='small'
            sx={{ color: theme.palette.error.main }}
          />
        )}
        <Typography
          variant='body2'
          sx={{
            color: hasAlerts ? theme.palette.error.main : 'inherit',
            fontWeight: hasAlerts ? 600 : 400,
            cursor: hasAlerts ? 'pointer' : 'default',
          }}
        >
          {formatNumber(count, 0)}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const ApplicationsOverviewWidget = ({ pageSize = 5 }) => {
  const theme = useTheme();
  const ability = useContext(AbilityContext);
  const { selectedGroupId, setSelectedGroupId } =
    useSelectedApplicationGroupId();
  const { setSelectedApplicationId: setSelectedAppId } =
    useSelectedApplicationId();
  const { navigateToApplicationDashboard } = useAdminUrlNavigation();
  const { navigateWithApplicationContext } = useNavigateWithState();
  const { utility } = useDashboard();

  /* States */
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {}
  );
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const FULLSCREEN_PAGE_SIZE = 100;
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    string | null
  >(null);
  const [selectedApplicationName, setSelectedApplicationName] = useState<
    string | null
  >(null);
  const [fullscreenPagination, setFullscreenPagination] =
    useState<MRT_PaginationState>({
      pageIndex: 0,
      pageSize: FULLSCREEN_PAGE_SIZE,
    });

  const columnMenuOpen = Boolean(columnMenuAnchorEl);
  const isGroupSelected =
    selectedGroupId !== undefined &&
    selectedGroupId !== null &&
    !isAllSelection(selectedGroupId);

  /* API Calls */
  const canReadApplications = ability.can(ACTIONS.READ, 'Applications');
  const canReadAlerts = ability.can(ACTIONS.READ, 'Alerts');

  const {
    data: overviewData,
    isLoading,
    isError,
    isFetching,
  } = useGetApplicationGroupOverviewQuery(
    {
      applicationGroupId: selectedGroupId || undefined,
      // No limit specified - uses backend default (5) but filtered to single group
      // Returns all applications in that group as subRows
    },
    {
      skip: !isGroupSelected || !canReadApplications,
    }
  );

  // Query for full alert details (needed for modal)
  const { data: alertsData, isLoading: isAlertsLoading } = useAlertFindQuery(
    {
      applicationGroupId: selectedGroupId || undefined,
      state: AlertState.Active,
      limit: '0',
    },
    {
      skip: !isGroupSelected || !canReadAlerts,
    }
  );

  // Extract applications from the group overview
  const applicationsData = useMemo(() => {
    if (!overviewData?.data?.[0]?.applications) return [];
    return overviewData.data[0].applications;
  }, [overviewData]);

  /* Functions & Functional Components */
  const handleNavigateToApplication = useCallback(
    (groupId: string, appId: string) => {
      setSelectedGroupId(groupId);
      setSelectedAppId(appId);
      navigateToApplicationDashboard(appId, groupId);
    },
    [navigateToApplicationDashboard, setSelectedGroupId, setSelectedAppId]
  );

  const handleNavigateToMetersOrGateways = useCallback(
    (groupId: string, appId: string, path: string) => {
      navigateWithApplicationContext(groupId, appId, path);
    },
    [navigateWithApplicationContext]
  );

  const handleViewAlerts = useCallback(
    (applicationId: string, applicationName: string) => {
      setSelectedApplicationId(applicationId);
      setSelectedApplicationName(applicationName);
      setAlertsModalOpen(true);
    },
    []
  );

  const handleCloseAlertsModal = useCallback(() => {
    setAlertsModalOpen(false);
    setSelectedApplicationId(null);
    setSelectedApplicationName(null);
  }, []);

  // To Keep table alert counts aligned with navbar by deriving from alertFind data.
  const alertsCountByApplicationId = useMemo(() => {
    const counts = new Map<string, number>();
    if (!alertsData?.data?.length) return counts;

    for (const alert of alertsData.data) {
      const applicationId = alert.scope?.applicationId;
      if (!applicationId) continue;
      counts.set(applicationId, (counts.get(applicationId) ?? 0) + 1);
    }

    return counts;
  }, [alertsData]);

  const selectedApplicationAlerts = useMemo(() => {
    if (!selectedApplicationId || !alertsData?.data) return [];
    return alertsData.data.filter(
      (alert) => alert.scope?.applicationId === selectedApplicationId
    );
  }, [selectedApplicationId, alertsData]);

  const handleColumnMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setColumnVisibility((prev) => {
      const currentValue = prev[columnId as keyof typeof prev];
      const newValue = currentValue === false ? true : false;
      return {
        ...prev,
        [columnId]: newValue,
      };
    });
  };

  const handleBasePaginationChange = useCallback(
    (
      updater:
        | MRT_PaginationState
        | ((prev: MRT_PaginationState) => MRT_PaginationState)
    ) => {
      setPagination((prev) =>
        typeof updater === 'function'
          ? (updater as (arg: MRT_PaginationState) => MRT_PaginationState)(prev)
          : updater
      );
    },
    []
  );

  const handleFullscreenPaginationChange = useCallback(
    (
      updater:
        | MRT_PaginationState
        | ((prev: MRT_PaginationState) => MRT_PaginationState)
    ) => {
      setFullscreenPagination((prev) =>
        typeof updater === 'function'
          ? (updater as (arg: MRT_PaginationState) => MRT_PaginationState)(prev)
          : updater
      );
    },
    []
  );

  const tableData: TableRow[] = useMemo(() => {
    if (!applicationsData?.length) return [];

    return applicationsData.map((app) => ({
      _id: app.applicationId,
      name: app.applicationName,
      groupId: selectedGroupId || '',
      meters: app.meters,
      gateways: app.gateways,
      activeAlerts:
        alertsCountByApplicationId.get(app.applicationId) ??
        app.activeAlerts ??
        0,
      assignedUsers: app.assignedUsers,
    }));
  }, [applicationsData, selectedGroupId, alertsCountByApplicationId]);

  const columns = useMemo<MRT_ColumnDef<TableRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 320,
        minSize: 320,
        maxSize: 320,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const handleNavigateClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            handleNavigateToApplication(row.original.groupId, row.original._id);
          };

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {row.original.name}
              </Typography>
              <Tooltip
                slotProps={{ tooltip: { sx: { mt: '5px !important' } } }}
                title='Select Application'
              >
                <IconButton
                  size='small'
                  onClick={handleNavigateClick}
                  sx={{
                    my: -1,
                    color: theme.palette.primary.main,
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity 150ms ease',
                    '.MuiTableRow-root:hover &': {
                      opacity: 1,
                      pointerEvents: 'auto',
                    },
                  }}
                >
                  <ArrowForward fontSize='small' />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        accessorKey: 'meters.total',
        header: 'Meters',
        enableColumnFilter: false,
        size: 140,
        Cell: ({ row }) => (
          <MetricCell
            metrics={row.original.meters}
            type='meters'
            groupId={row.original.groupId}
            applicationId={row.original._id}
            utilityType={utility}
            onNavigate={handleNavigateToMetersOrGateways}
          />
        ),
      },
      {
        accessorKey: 'gateways.total',
        header: 'Gateways',
        enableColumnFilter: false,
        size: 140,
        Cell: ({ row }) => (
          <MetricCell
            metrics={row.original.gateways}
            type='gateways'
            groupId={row.original.groupId}
            applicationId={row.original._id}
            onNavigate={handleNavigateToMetersOrGateways}
          />
        ),
      },
      {
        accessorKey: 'activeAlerts',
        header: 'Alerts',
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => (
          <AlertCell
            count={row.original.activeAlerts}
            applicationId={row.original._id}
            applicationName={row.original.name}
            onViewAlerts={handleViewAlerts}
          />
        ),
      },
      {
        accessorKey: 'assignedUsers',
        header: 'Users',
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => {
          return (
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.5}
              justifyContent='flex-start'
            >
              <Groups
                fontSize='small'
                sx={{ color: theme.palette.text.secondary }}
              />
              <Typography variant='body2'>
                {row.original.assignedUsers ?? 0}
              </Typography>
            </Stack>
          );
        },
      },
    ],
    [
      handleNavigateToMetersOrGateways,
      handleViewAlerts,
      handleNavigateToApplication,
    ]
  );

  const columnIds = columns.map((col) => ({
    id: col.accessorKey as string,
    header:
      typeof col.header === 'string' ? col.header : (col.accessorKey as string),
  }));

  const renderRowActions = (row: MRT_Row<TableRow>, closeMenu: () => void) => [
    <MenuItem
      key='view'
      onClick={() => {
        handleNavigateToApplication(row.original.groupId, row.original._id);
        closeMenu();
      }}
      disabled={!canReadApplications}
    >
      <ListItemIcon>
        <ArrowForward color='primary' />
      </ListItemIcon>
      <ListItemText>Select Application</ListItemText>
    </MenuItem>,
  ];

  const actions = (
    <>
      <Tooltip
        key='columns-tooltip'
        placement='bottom'
        title='Show/Hide columns'
      >
        <IconButton
          key='columns'
          aria-controls={columnMenuOpen ? 'column-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={columnMenuOpen ? 'true' : undefined}
          onClick={handleColumnMenuClick}
          size='small'
        >
          <ViewColumn />
        </IconButton>
      </Tooltip>
      <Menu
        key='column-menu'
        id='column-menu'
        anchorEl={columnMenuAnchorEl}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        MenuListProps={{
          'aria-labelledby': 'column-button',
        }}
      >
        {columnIds.map((column) => (
          <MenuItem
            key={column.id}
            sx={{ height: theme.tokens.spacing.xl * 1.3 }}
            onClick={() => handleColumnToggle(column.id)}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    columnVisibility[
                      column.id as keyof typeof columnVisibility
                    ] !== false
                  }
                />
              }
              label={column.header}
              sx={{ pointerEvents: 'none', width: '100%' }}
            />
          </MenuItem>
        ))}
      </Menu>
      <Tooltip key='fullscreen-tooltip' title='Fullscreen'>
        <IconButton
          key='fullscreen'
          onClick={(e) => {
            e.stopPropagation();
            setIsFullscreen(true);
          }}
          size='small'
        >
          <Fullscreen />
        </IconButton>
      </Tooltip>
    </>
  );

  const tableComponent = (isFullscreenMode: boolean) => (
    <SwitchTable<TableRow>
      title='Applications'
      columns={columns}
      data={tableData}
      manualPagination={false}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      enableTopToolbar={false}
      enableBottomToolbar={true}
      enableRowActions={true}
      renderRowActionMenuItems={({ row, closeMenu }) =>
        renderRowActions(row, closeMenu)
      }
      muiTablePaperProps={{
        sx: { maxHeight: isFullscreenMode ? '100%' : 'auto' },
      }}
      muiPaginationProps={{
        rowsPerPageOptions: [5, 10, 15, 20, 50, 100],
      }}
      state={{
        sorting,
        pagination: isFullscreenMode ? fullscreenPagination : pagination,
        columnVisibility,
      }}
      initialState={{
        density: 'compact',
        pagination: isFullscreenMode ? fullscreenPagination : pagination,
      }}
      onSortingChange={setSorting}
      onPaginationChange={
        isFullscreenMode
          ? handleFullscreenPaginationChange
          : handleBasePaginationChange
      }
      onColumnVisibilityChange={setColumnVisibility}
    />
  );

  return (
    <>
      <WidgetCard
        title='Applications Overview'
        subheader='Application metrics and statuses'
        avatar={
          <Box
            sx={{
              padding: `${theme.tokens.spacing.sm}px`,
              height: `${theme.tokens.spacing.sm * 5}px`,
              width: `${theme.tokens.spacing.sm * 5}px`,
              mx: `-${theme.tokens.spacing.sm}px`,
              borderRadius: theme.tokens.borderRadius.full,
              background: theme.tokens.colors.card,
              boxShadow: theme.tokens.shadows.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.primary.main,
            }}
          >
            <AppsOutlined />
          </Box>
        }
        action={actions}
        cardContentProps={{
          sx: {
            display: 'flex',
            flex: 1,
            height: '80%',
            width: '100%',
            flexDirection: 'column',
            background: theme.colors.neutral.grey[20],
            borderTop: `1px solid ${theme.tokens.extendedColors.borders.light}`,
            paddingBottom: '0px !important',
            px: 0,
          },
        }}
      >
        {tableComponent(false)}
      </WidgetCard>

      {/* Fullscreen Modal */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        fullScreen
        PaperProps={{
          sx: {
            background: theme.colors.neutral.grey[20],
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 3 },
              py: { xs: 1.5, md: 2 },
              borderBottom: `1px solid ${theme.tokens.colors.border}`,
              background: lighten(theme.palette.primary.main, 0.95),
            }}
          >
            <Box>
              <Typography
                variant={'h1'}
                fontSize={{
                  xs: theme.tokens.typography.fontSize.lg,
                  md: theme.tokens.typography.fontSize.xl,
                }}
                fontFamily={theme.tokens.typography.fontFamily.display}
                color={theme.palette.primary.main}
                fontWeight={theme.tokens.typography.fontWeight.semibold}
              >
                Applications Overview
                {isFullscreen && overviewData?.data?.[0]?.applicationGroupName
                  ? ` - ${overviewData.data[0].applicationGroupName}`
                  : ''}
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: theme.tokens.colors.mutedForeground }}
              >
                Overview of all applications with metrics
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title='Show/Hide columns'>
                <IconButton onClick={handleColumnMenuClick}>
                  <ViewColumn />
                </IconButton>
              </Tooltip>
              <Tooltip title='Close fullscreen'>
                <IconButton onClick={() => setIsFullscreen(false)}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Table */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
            }}
          >
            {tableComponent(true)}
          </Box>
        </Box>
      </Dialog>

      <AlertsModal
        open={alertsModalOpen}
        onClose={handleCloseAlertsModal}
        alerts={selectedApplicationAlerts}
        title={
          selectedApplicationName
            ? `Active Alerts - ${selectedApplicationName}`
            : 'Active Alerts'
        }
        isLoading={isAlertsLoading}
      />
    </>
  );
};

export default ApplicationsOverviewWidget;
